import { useEffect, useState } from "react"
import { FilterProps } from "../../../../../../hooks/filter/typing"
import { removeAllReference } from "../../../../../../utils/methods/removedReference"
import { InputModel } from "../../../../../../utils/types"
import { Item } from "../../../../../../utils/types/generals"
import { useDashboardMainContext } from "../../provider"
import { currentWeek } from "../methods/calcDates"
import { Box, Typography } from "@mui/material"
import { intervaleList } from "../../../../../../utils/constants/intervaleList"
import { useTranslation } from "react-i18next"

const useInput = () => {
    const { items } = useDashboardMainContext()
    const [input, setInput] = useState<Record<string, InputModel>>({})
    const [externalInput, setExternalInput] = useState<Record<string, any>>({})
    const { t } = useTranslation()

    const monthsList = [
        { label: 'Enero', value: 1 },
        { label: 'Febrero', value: 2 },
        { label: 'Marzo', value: 3 },
        { label: 'Abril', value: 4 },
        { label: 'Mayo', value: 5 },
        { label: 'Junio', value: 6 },
        { label: 'Julio', value: 7 },
        { label: 'Agosto', value: 8 },
        { label: 'Septiembre', value: 9 },
        { label: 'Octubre', value: 10 },
        { label: 'Noviembre', value: 11 },
        { label: 'Diciembre', value: 12 }
    ]

    const externalValidation: FilterProps['externalValidation'] = ({ name, value, body, setFilterData }) => {
        let copyBody: Record<string, any> = removeAllReference(body)
        if (name === 'divisions') {
            let { divisions, zones: zonesData } = copyBody
            divisions = Array.isArray(divisions) ? divisions : []
            const newZones = zonesData?.filter((e: any) => divisions.find((e2: any) => e2.value === e.divisionId))
            let arrayChilds: any[] = []

            divisions.forEach((e: any) => {
                const id = e.value
                const childs = (items?.zones as Item[])?.filter((child) => child.divisionId === id)
                arrayChilds.push(...childs)
            })

            setInput(prev => ({ ...prev, zones: { ...prev.zones, items: arrayChilds, } }))
            setFilterData((prev) => ({
                ...prev,
                zones: newZones,
            }))

            copyBody = {
                ...copyBody,
                zones: newZones,
            }
        }
        return copyBody
    }

    const removeValues = () => {
        setInput(prev => ({
            ...prev,
            from: {
                ...prev.from,
                value: null
            },
            to: {
                ...prev.to,
                value: null
            },
        }))
    }

    useEffect(() => {
        setInput({
            year: {
                label: 'Año',
                name: 'year',
                type: 'select-search',
                items: Array.from({ length: 5 }, (_, i) => {
                    const year = new Date().getFullYear() - i
                    return { label: year.toString(), value: year }
                }),
                value: null,
                size: {
                    xl: 12,
                    lg: 12,
                    md: 12,
                    sm: 12,
                    xs: 12,
                },
                wrapChildren: ({ children, id }) => {
                    return (
                        <Box key={id}>
                            <Typography sx={{
                                fontSize: '16px',
                                fontWeight: '600',
                                color: '#707070',
                                marginBottom: '20px',
                            }}>Periodo</Typography>

                            {children}
                        </Box>
                    )
                }
            },
            month: {
                label: 'Mes',
                name: 'month',
                type: 'filter-select',
                items: monthsList,
                size: {
                    xl: 12,
                    lg: 12,
                    md: 12,
                    sm: 12,
                    xs: 12,
                }
            },
            from: {
                label: 'general.tags.from',
                translate: true,
                name: 'from',
                type: 'date',
                value: null,
                rest: {
                    max: 'to',
                },
                size: {
                    xl: 6,
                    lg: 6,
                    md: 6,
                    sm: 6,
                    xs: 6,
                },
            },
            to: {
                label: 'general.tags.to',
                translate: true,
                name: 'to',
                type: 'date',
                rest: {
                    min: 'from',
                },
                value: null,
                size: {
                    xl: 6,
                    lg: 6,
                    md: 6,
                    sm: 6,
                    xs: 6,
                },
                itemChildren: {
                    alignSelf: "end"
                }
            },
            questionnaire: {
                label: 'general.tags.questionnaires',
                translate: true,
                name: 'questionnaire',
                type: 'filter-select',
                items: items?.questionnaire as Item[] || [],
                value: null,
                wrapChildren: ({ children, id }) => {
                    return (
                        <Box key={id}>
                            <Typography sx={{
                                fontSize: '16px',
                                fontWeight: '600',
                                color: '#707070',
                                marginBottom: '20px',
                            }}>{t('general.filters')}</Typography>

                            {children}
                        </Box>
                    )
                }
            }
        })
    }, [items])


    useEffect(() => {
        setExternalInput({
            divisions: {
                label: 'general.tags.divisions',
                translate: true,
                name: 'divisions',
                type: 'filter-select',
                items: items?.division as Item[] || [],
                value: null,
                size: {
                    xl: 4,
                    lg: 4,
                    md: 12,
                    sm: 12,
                    xs: 12,
                },
                showAllInModal: true,
            },
            zones: {
                label: 'general.tags.zones',
                translate: true,
                name: 'zones',
                type: 'filter-select',
                items: items?.zones as Item[] || [],
                value: null,
                size: {
                    xl: 4,
                    lg: 4,
                    md: 12,
                    sm: 12,
                    xs: 12,
                },
                showAllInModal: true,
            },
            units: {
                label: 'general.tags.unit',
                translate: true,
                name: 'units',
                type: 'filter-select',
                items: items?.unit as Item[] || [],
                value: null,
                size: {
                    xl: 4,
                    lg: 4,
                    md: 12,
                    sm: 12,
                    xs: 12,
                },
                showAllInModal: true,
            },
        })
    }, [items])

    return {
        externalValidation,
        input,
        removeValues,
        items,
        externalInput,
    }
}



const exportColumns = [
    {
        id: 'division',
        label: 'División'
    },
    {
        id: 'zone',
        label: 'Zona'
    },
    {
        id: 'unit',
        label: 'Unidad'
    },
    {
        id: 'questionnaire',
        label: 'Cuestionario'
    },
    {
        id: 'results',
        label: 'Resultados'
    }
]

const downloadHeaderColumns = [
    { id: 'name', label: 'División' },
    { id: 'ene', label: 'Ene' },
    { id: 'feb', label: 'Feb' },
    { id: 'mar', label: 'Mar' },
    { id: 'abr', label: 'Abr' },
    { id: 'may', label: 'May' },
    { id: 'jun', label: 'Jun' },
    { id: 'jul', label: 'Jul' },
    { id: 'ago', label: 'Ago' },
    { id: 'sep', label: 'Sep' },
    { id: 'oct', label: 'Oct' },
    { id: 'nov', label: 'Nov' },
    { id: 'dic', label: 'Dic' },
    { id: 'total', label: 'Total' },
    { id: 'promedio', label: 'Promedio' },
]

const allColumns = [
    { field: 'name', headerName: 'División', width: 200, align: 'left' as const },
    { field: 'ene', headerName: 'Ene', width: 60, align: 'center' as const },
    { field: 'feb', headerName: 'Feb', width: 60, align: 'center' as const },
    { field: 'mar', headerName: 'Mar', width: 60, align: 'center' as const },
    { field: 'abr', headerName: 'Abr', width: 60, align: 'center' as const },
    { field: 'may', headerName: 'May', width: 60, align: 'center' as const },
    { field: 'jun', headerName: 'Jun', width: 60, align: 'center' as const },
    { field: 'jul', headerName: 'Jul', width: 60, align: 'center' as const },
    { field: 'ago', headerName: 'Ago', width: 60, align: 'center' as const },
    { field: 'sep', headerName: 'Sep', width: 60, align: 'center' as const },
    { field: 'oct', headerName: 'Oct', width: 60, align: 'center' as const },
    { field: 'nov', headerName: 'Nov', width: 60, align: 'center' as const },
    { field: 'dic', headerName: 'Dic', width: 60, align: 'center' as const },
    { field: 'total', headerName: 'Total', width: 150, align: 'center' as const },
    { field: 'promedio', headerName: 'Promedio', width: 150, align: 'center' as const },
]

const monthFields = [
    { value: 1, field: 'ene' },
    { value: 2, field: 'feb' },
    { value: 3, field: 'mar' },
    { value: 4, field: 'abr' },
    { value: 5, field: 'may' },
    { value: 6, field: 'jun' },
    { value: 7, field: 'jul' },
    { value: 8, field: 'ago' },
    { value: 9, field: 'sep' },
    { value: 10, field: 'oct' },
    { value: 11, field: 'nov' },
    { value: 12, field: 'dic' },
]

const MONTH_KEYS = [
    'ene', 'feb', 'mar', 'abr', 'may', 'jun',
    'jul', 'ago', 'sep', 'oct', 'nov', 'dic'
  ]

export { useInput, exportColumns, downloadHeaderColumns, allColumns, monthFields, MONTH_KEYS }
